import { useState } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Add as AddIcon } from "@mui/icons-material";
import { Button } from "@mui/material";
import type { GridSortModel } from "@mui/x-data-grid";
import useAsyncAbortableRequest from "hooks/useAsyncAbortableRequest";
import type { FilterValue } from "hooks/useFilters";
import useFilters from "hooks/useFilters";
import usePagination from "hooks/usePagination";
import useSorting from "hooks/useSorting";
import { LoadablePage } from "shared/LoadablePage";
import { BillingAccountTypeFilter } from "client/api-v2";
import FilterLayout from "components/FilterLayout";
import { FilterSectionType } from "components/FilterLayout/FilterSection";
import type { FilterSectionFieldProps } from "components/FilterLayout/FilterSection";
import { PageContent } from "components/PageContent/PageContent";
import PageHeading from "components/PageHeading";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { billingAccountRoutes } from "areas/billing-accounts/billing-account-routes";
import { BillingAccountDialog } from "areas/billing-accounts/components/BillingAccountDialog";
import { BillingAccountList } from "areas/billing-accounts/components/BillingAccountList";
import { getBillingAccounts } from "areas/billing-accounts/services/billingAccountService";

const headerSections: FilterSectionFieldProps[] = [
  {
    type: FilterSectionType.Text,
    filterKey: "search",
    label: "Search by name",
  },
  {
    type: FilterSectionType.ToggleGroup,
    filterKey: "type",
    label: "Account type",
    defaultValue: BillingAccountTypeFilter.All,
    toggleItems: [
      { value: BillingAccountTypeFilter.All, label: "All" },
      { value: BillingAccountTypeFilter.Customers, label: "Customers" },
      { value: BillingAccountTypeFilter.Resellers, label: "Resellers" },
    ],
  },
];

function isTypeFilter(value: FilterValue): value is BillingAccountTypeFilter {
  return typeof value === "string" && Object.values(BillingAccountTypeFilter).some((type) => type === value);
}

function CreateBillingAccountButton({ onCreate }: { onCreate: () => void }) {
  return (
    <Button
      id="create-billing-account-button"
      onClick={onCreate}
      color="secondary"
      variant="outlined"
      startIcon={<AddIcon />}
    >
      Create Billing Account
    </Button>
  );
}

export function BillingAccountsPage() {
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { page, setPage, pageSize, setPageSize } = usePagination();
  const { filters, setFilter, resetFilter } = useFilters({ filterMap: {} });
  const { sortModel, setSortModel } = useSorting();

  // Changing a filter changes the result set, so a page kept from the previous one could be past
  // the end of the new one — an empty grid with no explanation.
  const handleFilterChange = (key: string, value: FilterValue) => {
    setPage(0);
    setFilter(key, value);
  };

  const handleResetFilter = () => {
    setPage(0);
    resetFilter();
  };

  // Re-sorting reorders the whole result set, so a page held from the old order shows rows nobody
  // asked for — the same hazard as changing a filter.
  const handleSortModelChange = (model: GridSortModel) => {
    setPage(0);
    setSortModel(model);
  };

  const {
    data = { items: [], totalItems: 0 },
    loading,
    error,
  } = useAsyncAbortableRequest(getBillingAccounts, {
    search: typeof filters.search === "string" ? filters.search : undefined,
    type: isTypeFilter(filters.type) ? filters.type : undefined,
    page: page + 1, // Convert 0-based to 1-based page number
    pageSize,
    sortFieldName: sortModel[0]?.field,
    sortDirection: sortModel[0]?.sort === "asc" ? "Asc" : sortModel[0]?.sort === "desc" ? "Desc" : undefined,
  });

  return (
    <>
      <PageHeading
        headingText="Billing Accounts"
        actionItems={[
          {
            policy: RbacPolicies.BillingAccounts.ReadWrite,
            component: <CreateBillingAccountButton onCreate={() => setIsCreateDialogOpen(true)} />,
          },
        ]}
      />
      <PageContent>
        <FilterLayout
          headerFilters={headerSections}
          filterSections={[]}
          filter={filters}
          defaultFilter={{}}
          onFilterChange={handleFilterChange}
          onResetFilter={handleResetFilter}
        >
          <LoadablePage error={error}>
            <BillingAccountList
              billingAccounts={data.items}
              totalCount={data.totalItems}
              loading={loading}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
            />
          </LoadablePage>
        </FilterLayout>
        <BillingAccountDialog
          open={isCreateDialogOpen}
          onCancel={() => setIsCreateDialogOpen(false)}
          onSaved={(account) => {
            setIsCreateDialogOpen(false);
            navigate(billingAccountRoutes.detail(account.code).root);
          }}
        />
      </PageContent>
    </>
  );
}
